import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  BookOpen,
  Upload,
  Search,
  ArrowLeft,
  Calendar,
  Tag,
  FileText,
  Copy,
  X,
  Image as ImageIcon,
  ChevronRight,
  Pencil,
  Check
} from 'lucide-react';
import { notesApi, analyticsApi, tokenApi } from '@/lib/api';
import { toast } from 'sonner';
import { formatDate, fileToBase64, isAuthenticated } from '@/lib/utils';
import SponsorSection from '@/components/SponsorSection';
import { ModuleChat } from '@/components/ModuleChat';

export default function NotesModule() {
  const { tokenId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const [notes, setNotes] = useState([]);
  const [stats, setStats] = useState({ total_notes: 0, notes_this_week: 0 });
  const [sponsorInfo, setSponsorInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [showAddOptions, setShowAddOptions] = useState(false);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [uploadTags, setUploadTags] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [notebookName, setNotebookName] = useState(() => {
    return localStorage.getItem(`memora_notes_name_${tokenId}`) || 'Memora Notes';
  });
  const [isEditingName, setIsEditingName] = useState(false);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate(`/n/${tokenId}`);
      return;
    }
    loadData();
    loadSponsorInfo();
    logDashboardOpen();
  }, [tokenId]);

  const loadData = async () => {
    try {
      const [notesRes, statsRes] = await Promise.all([
        notesApi.getNotes(tokenId),
        notesApi.getStats(tokenId)
      ]);
      setNotes(Array.isArray(notesRes.data) ? notesRes.data : []);
      setStats(statsRes.data || { total_notes: 0, notes_this_week: 0 });
    } catch (error) {
      toast.error('Failed to load notes');
    } finally {
      setIsLoading(false);
    }
  };

  const loadSponsorInfo = async () => {
    try {
      const response = await tokenApi.getSponsorInfo(tokenId, 'notes');
      setSponsorInfo(response.data);
    } catch (error) {
      console.log('No sponsor info available');
    }
  };

  const logDashboardOpen = async () => {
    try {
      await analyticsApi.logEvent(tokenId, 'dashboard_open');
    } catch (e) {
      // Silent fail for analytics
    }
  };

  const getNoteText = (note) => {
    const text = note?.ocr_text || '';
    if (!text || text.startsWith('[OCR')) return 'No readable OCR text yet.';
    return text;
  };

  const getNoteTitle = (note) => {
    const text = getNoteText(note)
      .replace(/\s+/g, ' ')
      .trim();

    if (!text || text === 'No readable OCR text yet.') return 'Untitled note';

    return text.length > 72 ? `${text.slice(0, 72)}...` : text;
  };

  const getNoteExcerpt = (note) => {
    const text = getNoteText(note)
      .replace(/\s+/g, ' ')
      .trim();

    if (!text) return 'No extracted content available.';

    return text.length > 180 ? `${text.slice(0, 180)}...` : text;
  };

  const getImageSrc = (note) => {
    if (!note) return null;

    if (note.image_base64) {
      if (note.image_base64.startsWith('data:image')) return note.image_base64;
      return `data:image/jpeg;base64,${note.image_base64}`;
    }

    if (note.image_url?.startsWith('data:image') && !note.image_url.includes('...')) {
      return note.image_url;
    }

    if (note.image_url?.startsWith('http')) {
      return note.image_url;
    }

    return null;
  };

  const handleCopyText = async () => {
    if (!selectedNote?.ocr_text) {
      toast.error('No text to copy');
      return;
    }

    try {
      await navigator.clipboard.writeText(selectedNote.ocr_text);
      toast.success('Extracted text copied');
    } catch (error) {
      toast.error('Failed to copy text');
    }
  };

  const saveNotebookName = () => {
    const cleanName = notebookName.trim() || 'Memora Notes';
    setNotebookName(cleanName);
    localStorage.setItem(`memora_notes_name_${tokenId}`, cleanName);
    setIsEditingName(false);
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';

    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    try {
      const base64 = await fileToBase64(file);
      setUploadPreview({ file, base64 });
      setShowUpload(true);
    } catch (error) {
      toast.error('Failed to process image');
    }
  };

  const handleUpload = async () => {
    if (!uploadPreview) return;

    setIsUploading(true);
    try {
      const tags = uploadTags.split(',').map(t => t.trim()).filter(Boolean);
      await notesApi.createNote(tokenId, uploadPreview.base64, tags);
      toast.success('Note uploaded and processed');
      setShowUpload(false);
      setUploadPreview(null);
      setUploadTags('');
      loadData();
    } catch (error) {
      toast.error('Failed to upload note');
    } finally {
      setIsUploading(false);
    }
  };

  const filteredNotes = notes.filter(note => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return true;

    return (
      note.ocr_text?.toLowerCase().includes(q) ||
      note.tags?.some(tag => tag.toLowerCase().includes(q)) ||
      formatDate(note.created_at).toLowerCase().includes(q)
    );
  });

  return (
    <div className="min-h-screen bg-slate-50" data-testid="notes-module">
      <header className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/dashboard" className="text-slate-400 hover:text-slate-600 transition-colors shrink-0" data-testid="back-to-dashboard">
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center shrink-0">
              <BookOpen className="w-5 h-5 text-blue-600" />
            </div>
            <div className="min-w-0">
              {isEditingName ? (
                <div className="flex items-center gap-2">
                  <Input
                    value={notebookName}
                    onChange={(e) => setNotebookName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveNotebookName();
                      if (e.key === 'Escape') setIsEditingName(false);
                    }}
                    className="h-9 text-lg font-bold rounded-xl bg-slate-50 border-slate-200"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={saveNotebookName}
                    className="w-9 h-9 rounded-full bg-blue-600 text-white flex items-center justify-center shrink-0"
                    aria-label="Save notebook name"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setIsEditingName(true)}
                  className="group flex items-center gap-2 text-left max-w-full"
                  aria-label="Rename notebook"
                >
                  <h1 className="font-bold text-lg text-slate-900 truncate">{notebookName}</h1>
                  <Pencil className="w-4 h-4 text-slate-300 group-hover:text-blue-500 shrink-0" />
                </button>
              )}
            </div>
          </div>

          <Button
            onClick={() => setShowAddOptions(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-4 sm:px-6 shrink-0"
            data-testid="upload-note-btn"
          >
            <Upload className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Add Page</span>
          </Button>

          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={handleFileSelect}
            data-testid="camera-input"
          />

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileSelect}
            data-testid="file-input"
          />
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6">

        <SponsorSection
          sponsorInfo={sponsorInfo}
          tokenId={tokenId}
          moduleType="notes"
          className="mb-5"
        />

        <div className="bg-white border border-slate-100 rounded-2xl p-4 mb-5">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Search OCR text, tags, or date..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 bg-slate-50 border-slate-200 rounded-xl h-12"
              data-testid="search-notes-input"
            />
          </div>
          <div className="flex items-center justify-between mt-3 text-xs text-slate-500">
            <span>
              {filteredNotes.length} notes • {stats.notes_this_week} added this week
            </span>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                Clear search
              </button>
            )}
          </div>
        </div>

        {isLoading ? (
          <div className="bg-white border border-slate-100 rounded-2xl divide-y divide-slate-100 overflow-hidden">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="p-4 animate-pulse">
                <div className="h-4 bg-slate-200 rounded w-2/3 mb-3" />
                <div className="h-3 bg-slate-100 rounded w-full mb-2" />
                <div className="h-3 bg-slate-100 rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : filteredNotes.length === 0 ? (
          <motion.div
            className="bg-white rounded-2xl p-10 text-center border border-slate-100"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-5">
              <BookOpen className="w-8 h-8 text-blue-400" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2">
              {searchQuery ? 'No matching notes' : 'No notes yet'}
            </h3>
            <p className="text-slate-600 mb-6 max-w-md mx-auto">
              {searchQuery
                ? 'Try another keyword, tag, or date.'
                : 'Upload your first notebook page to make your handwritten notes searchable.'}
            </p>
            {!searchQuery && (
              <Button
                onClick={() => setShowAddOptions(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-8"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload First Page
              </Button>
            )}
          </motion.div>
        ) : (
          <div className="bg-white border border-slate-100 rounded-2xl divide-y divide-slate-100 overflow-hidden">
            {filteredNotes.map((note, index) => {
              const imageSrc = getImageSrc(note);

              return (
                <motion.button
                  type="button"
                  key={note.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(index * 0.025, 0.2) }}
                  onClick={() => setSelectedNote(note)}
                  className="w-full text-left p-4 hover:bg-slate-50 transition-colors flex items-start gap-4"
                  data-testid={`note-row-${note.id}`}
                >
                  <div className="w-14 h-14 rounded-xl bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center shrink-0">
                    {imageSrc ? (
                      <img src={imageSrc} alt="Note thumbnail" className="w-full h-full object-cover" />
                    ) : (
                      <FileText className="w-6 h-6 text-slate-400" />
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="font-semibold text-slate-900 text-sm sm:text-base line-clamp-1">
                        {getNoteTitle(note)}
                      </h3>
                      <ChevronRight className="w-4 h-4 text-slate-300 shrink-0 mt-0.5" />
                    </div>

                    <p className="text-sm text-slate-600 mt-1 line-clamp-2 leading-relaxed">
                      {getNoteExcerpt(note)}
                    </p>

                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-3 text-xs text-slate-500">
                      <span className="inline-flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        {formatDate(note.created_at)}
                      </span>

                      {note.tags?.length > 0 && (
                        <span className="inline-flex items-center gap-1 text-blue-600">
                          <Tag className="w-3.5 h-3.5" />
                          {note.tags.slice(0, 2).join(', ')}
                          {note.tags.length > 2 ? ` +${note.tags.length - 2}` : ''}
                        </span>
                      )}
                    </div>
                  </div>
                </motion.button>
              );
            })}
          </div>
        )}
      </main>

      <ModuleChat sponsorInfo={sponsorInfo} />


      <Dialog open={showAddOptions} onOpenChange={setShowAddOptions}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Notebook Page</DialogTitle>
          </DialogHeader>

          <div className="grid gap-3 mt-4">
            <Button
              type="button"
              onClick={() => {
                setShowAddOptions(false);
                cameraInputRef.current?.click();
              }}
              className="h-14 rounded-2xl bg-blue-600 hover:bg-blue-700 text-white justify-start px-5"
            >
              <ImageIcon className="w-5 h-5 mr-3" />
              Take Photo
            </Button>

            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowAddOptions(false);
                fileInputRef.current?.click();
              }}
              className="h-14 rounded-2xl justify-start px-5"
            >
              <Upload className="w-5 h-5 mr-3" />
              Upload Image
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Upload Notebook Page</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {uploadPreview && (
              <div className="relative aspect-[4/3] rounded-xl overflow-hidden bg-slate-100">
                <img
                  src={`data:image/jpeg;base64,${uploadPreview.base64}`}
                  alt="Preview"
                  className="w-full h-full object-contain"
                />
                <button
                  onClick={() => setUploadPreview(null)}
                  className="absolute top-2 right-2 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">
                Tags (comma separated)
              </label>
              <Input
                placeholder="meeting, ideas, project..."
                value={uploadTags}
                onChange={(e) => setUploadTags(e.target.value)}
                className="bg-slate-50 border-slate-200 rounded-xl"
                data-testid="tags-input"
              />
            </div>

            <Button
              onClick={handleUpload}
              disabled={isUploading || !uploadPreview}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-full"
              data-testid="confirm-upload-btn"
            >
              {isUploading ? 'Processing with OCR...' : 'Upload & Extract Text'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!selectedNote} onOpenChange={() => setSelectedNote(null)}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-3 pr-8">
              <span>Note Result</span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleCopyText}
                className="rounded-full"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy Text
              </Button>
            </DialogTitle>
          </DialogHeader>

          {selectedNote && (
            <ScrollArea className="mt-4 max-h-[72vh] pr-4">
              <div className="space-y-5">
                <div className="bg-slate-50 border border-slate-100 rounded-2xl p-4">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">
                    Extracted Text
                  </h4>
                  <p className="text-slate-800 whitespace-pre-wrap leading-relaxed text-sm sm:text-base">
                    {selectedNote.ocr_text || 'No text extracted'}
                  </p>
                </div>

                {getImageSrc(selectedNote) ? (
                  <div>
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">
                      Original Page
                    </h4>
                    <div className="rounded-2xl overflow-hidden bg-slate-100 border border-slate-100">
                      <img
                        src={getImageSrc(selectedNote)}
                        alt="Original note"
                        className="w-full max-h-[55vh] object-contain"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="rounded-2xl border border-dashed border-slate-200 p-6 text-center text-slate-500">
                    <ImageIcon className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                    Original image is not available for this note.
                  </div>
                )}

                {selectedNote.tags?.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">
                      Tags
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedNote.tags.map(tag => (
                        <span key={tag} className="px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-sm">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <p className="text-xs text-slate-400">
                  Created {formatDate(selectedNote.created_at)}
                </p>
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
